/**
 * The merge rule. This is the part of multiplayer that is impossible to debug later.
 *
 * PLAN.md §4.1 and RESEARCH.md §5 settle the model: **not a CRDT**. Figma, tldraw and Excalidraw
 * all shipped multiplayer and all three converged, independently, on per-record last-writer-wins
 * with a version counter. Excalidraw's entire conflict rule is one predicate. So is this one, and
 * everything in this file exists to make that predicate total, pure and testable.
 *
 * ── The rule, in full ──────────────────────────────────────────────────────────────────────
 *
 * Two records for the same object id. The winner is decided in this order, and the first
 * difference decides it:
 *
 *   1. **Higher `version` wins.** A version is bumped by every edit, so this is "whoever knew
 *      more".
 *   2. **At equal version, a delete beats an edit.** Deleting and editing at the same moment is a
 *      genuine tie, and resurrecting an object someone has just deleted is the more surprising of
 *      the two outcomes — the person who deleted it looks at a sheet that ignored them. The
 *      person whose edit was dropped still has it in their own undo stack.
 *   3. **At equal version and equal deletedness, the LOWER `versionNonce` wins.** A random
 *      tiebreak both peers can compute. Excalidraw's rule exactly.
 *   4. **At an exact tie, the lexicographically lower canonical form wins.** Only reachable on a
 *      nonce collision (~2⁻³²) with differing content, and it is here so that convergence is
 *      unconditional rather than merely overwhelmingly likely. Nothing else depends on it.
 *
 * The rule is a total order on records, so merging is **commutative, associative and
 * idempotent**: the same set of commits, delivered in any order, any number of times, produces
 * the same scene on every peer. That is what makes a lossy, reordering, duplicating transport
 * safe, and it is why `tests/merge.test.ts` can prove convergence by shuffling.
 *
 * Wall-clock time is deliberately NOT part of the rule. Two laptops disagree about what time it
 * is, and a merge that consults `Date.now()` is a merge that behaves differently depending on
 * whose clock is fast. `updatedAt` is carried for display and for nothing else.
 *
 * ── Undo ───────────────────────────────────────────────────────────────────────────────────
 *
 * The single most important behavioural rule here: **local undo never undoes a remote peer's
 * work.** An undo is not a rewind of the shared document — it is a NEW commit that puts back what
 * you had, and it is scoped, per object, to the objects that are still yours to take back. If a
 * peer has edited an object since your commit wrote it, that object is skipped: taking it back
 * would silently discard their work, which is worse than an undo that does slightly less than you
 * expected. See `applyUndo` and the `skipped` list it returns.
 */

import { inPaintOrder, type Id, type Scene, type SceneObject } from './scene';
import { byKey, initialKeys, keyAfter, keysBetween } from './sortkey';

// ── Identity ───────────────────────────────────────────────────────────────────────────────

export type PeerId = string;

const randomBytes = (length: number): Uint8Array => {
  const out = new Uint8Array(length);
  const source = globalThis.crypto;
  if (source && typeof source.getRandomValues === 'function') return source.getRandomValues(out);
  // Node before webcrypto, and any exotic embedder. Ids stay unique enough to not collide within
  // a room; nothing here is a secret, so a weaker source is a degradation and not a hole.
  for (let i = 0; i < length; i++) out[i] = Math.floor(Math.random() * 256);
  return out;
};

let lastMillis = 0;
let sameMillis = 0;

/**
 * UUIDv7 — a time-ordered id generated on the client. PLAN.md §4.4.1.
 *
 * 48 bits of Unix milliseconds, then a 12-bit counter, then 62 bits of randomness. The counter is
 * what makes two objects created inside the same millisecond still sort in the order they were
 * made, which matters because a fast pen commits several strokes per tick and "sortable by
 * creation time" has to be true at that resolution to be worth anything.
 */
export function uuidv7(now = Date.now()): string {
  if (now === lastMillis) {
    sameMillis = (sameMillis + 1) & 0xfff;
  } else {
    lastMillis = now;
    sameMillis = 0;
  }

  const bytes = randomBytes(16);
  bytes[0] = (now / 2 ** 40) & 0xff;
  bytes[1] = (now / 2 ** 32) & 0xff;
  bytes[2] = (now / 2 ** 24) & 0xff;
  bytes[3] = (now / 2 ** 16) & 0xff;
  bytes[4] = (now / 2 ** 8) & 0xff;
  bytes[5] = now & 0xff;
  bytes[6] = 0x70 | ((sameMillis >> 8) & 0x0f);
  bytes[7] = sameMillis & 0xff;
  bytes[8] = 0x80 | (bytes[8]! & 0x3f);

  const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** The tiebreak of PLAN.md §4.4.3. 32 bits: enough that a tie is a curiosity, not a plan. */
export const versionNonce = (): number => {
  const bytes = randomBytes(4);
  return ((bytes[0]! << 24) | (bytes[1]! << 16) | (bytes[2]! << 8) | bytes[3]!) >>> 0;
};

// ── The replicated record ──────────────────────────────────────────────────────────────────

/**
 * One object, as the network sees it.
 *
 * The wrapper exists because `SceneObject` deliberately carries no sync metadata — a sheet that
 * has never been shared should not pay for fields it does not use, and scene.ts stays the pure
 * drawing model it is. Everything multiplayer needs lives out here.
 *
 * A deleted record keeps its object rather than nulling it. A tombstone that still knows what it
 * was can be un-deleted by an undo, can be shown in a "recently deleted" list, and makes the
 * merge symmetric — a delete and an edit are then the same shape of write and compare by the same
 * rule.
 */
export interface SyncRecord {
  id: Id;
  object: SceneObject;
  deleted: boolean;
  /** Bumped by every write. Rule 1. */
  version: number;
  /** Random, redrawn on every write. Rule 3. */
  versionNonce: number;
  /** Fractional z-order key — see sortkey.ts. Sorting by this string is paint order. */
  index: string;
  updatedBy: PeerId;
  /** Display only. Never consulted by the merge; see the header. */
  updatedAt: number;
}

/**
 * The replicated state of one sheet.
 *
 * `seen` makes duplicate delivery visibly a no-op rather than merely harmlessly idempotent. The
 * version rule alone would already absorb a repeat, but a transport that redelivers should be
 * able to say so, and a re-applied commit that reported `changed` would send everyone chasing a
 * bug that is not there.
 */
export interface SyncScene {
  records: Record<Id, SyncRecord>;
  seen: string[];
}

/** How many commit ids to remember. Comfortably longer than any plausible redelivery window. */
const SEEN_LIMIT = 512;

export const emptySync = (): SyncScene => ({ records: {}, seen: [] });

/**
 * A durable message: a set of record writes that apply together.
 *
 * Together matters. The area eraser removes one stroke and adds two, and a peer that saw the
 * removal without the additions would be looking at a hole. `label` is the same string the local
 * undo stack uses, so both sides describe what happened in the same words.
 */
export interface Commit {
  id: string;
  room: string;
  origin: PeerId;
  /** Per-origin and monotonic. A gap means a message was lost; see `Transport.since`. */
  seq: number;
  at: number;
  label: string;
  records: SyncRecord[];
}

// ── The rule ───────────────────────────────────────────────────────────────────────────────

/**
 * A canonical string for a record's content, with object keys sorted.
 *
 * Only rule 4 uses it. Sorted keys because the same object reaches two peers by different routes
 * — one built it, the other parsed it out of JSON — and a comparison that depended on key order
 * would decide differently on the two machines, which is the exact failure this tiebreak exists
 * to remove.
 */
export function canonical(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(',')}}`;
}

const content = (record: SyncRecord): string =>
  canonical({ object: record.object, deleted: record.deleted, index: record.index });

/**
 * Which of two records for the same id survives. Positive means `a`, negative means `b`, zero
 * means they are the same write.
 *
 * Total, deterministic, and computable by both peers from the records alone — no clock, no
 * server, no knowledge of what else has happened.
 */
export function compareRecords(a: SyncRecord, b: SyncRecord): number {
  if (a.version !== b.version) return a.version - b.version;
  if (a.deleted !== b.deleted) return a.deleted ? 1 : -1;
  if (a.versionNonce !== b.versionNonce) return b.versionNonce - a.versionNonce;
  const ca = content(a);
  const cb = content(b);
  if (ca === cb) return 0;
  return ca < cb ? 1 : -1;
}

/** True when `incoming` should replace `local`. */
export const beats = (incoming: SyncRecord, local: SyncRecord | undefined): boolean =>
  local === undefined || compareRecords(incoming, local) > 0;

export interface MergeResult {
  scene: SyncScene;
  /** Ids the commit actually changed. Empty means the commit told us nothing new. */
  applied: Id[];
  /** Ids the commit tried to change and lost. Not an error — this is the rule working. */
  rejected: Id[];
  /** The commit had already been applied. */
  duplicate: boolean;
}

/**
 * Merge one commit into a scene. Pure: the input scene is never mutated.
 *
 * Returns the same scene object by reference when nothing changed, so a caller can skip a repaint
 * on identity alone.
 */
export function mergeCommit(scene: SyncScene, commit: Commit): MergeResult {
  if (scene.seen.includes(commit.id)) {
    return { scene, applied: [], rejected: [], duplicate: true };
  }

  const applied: Id[] = [];
  const rejected: Id[] = [];
  let records = scene.records;

  for (const incoming of commit.records) {
    if (beats(incoming, records[incoming.id])) {
      if (records === scene.records) records = { ...scene.records };
      records[incoming.id] = incoming;
      applied.push(incoming.id);
    } else {
      rejected.push(incoming.id);
    }
  }

  const seen = [...scene.seen, commit.id];
  if (seen.length > SEEN_LIMIT) seen.splice(0, seen.length - SEEN_LIMIT);

  return { scene: { records, seen }, applied, rejected, duplicate: false };
}

/**
 * Fold a backlog in. The order of `commits` does not matter — that is the guarantee, and
 * `tests/merge.test.ts` proves it by shuffling.
 */
export function mergeAll(scene: SyncScene, commits: readonly Commit[]): MergeResult {
  let current = scene;
  const applied = new Set<Id>();
  const rejected = new Set<Id>();
  let duplicates = 0;
  for (const commit of commits) {
    const result = mergeCommit(current, commit);
    current = result.scene;
    for (const id of result.applied) applied.add(id);
    for (const id of result.rejected) rejected.add(id);
    if (result.duplicate) duplicates += 1;
  }
  return {
    scene: current,
    applied: [...applied],
    rejected: [...rejected].filter(id => !applied.has(id)),
    duplicate: duplicates === commits.length && commits.length > 0,
  };
}

// ── Projection, both ways ──────────────────────────────────────────────────────────────────

/**
 * The scene the renderer draws: alive records, in key order, with `z` filled in from the order.
 *
 * `z` is derived rather than stored. The fractional key is what the peers agree on; the integer
 * is a rendering detail the existing canvas code wants, and regenerating it here means nothing
 * upstream has to learn about sort keys.
 */
export function toScene(sync: SyncScene): Scene {
  const alive = Object.values(sync.records)
    .filter(record => !record.deleted)
    .sort(byKey);
  return { objects: alive.map((record, i) => ({ ...record.object, z: i + 1 })) };
}

/** Every record, deleted included — what a "recently deleted" list or a debug view wants. */
export const allRecords = (sync: SyncScene): SyncRecord[] => Object.values(sync.records).sort(byKey);

/**
 * Adopt a local sheet into sync-land: paint order becomes sort keys, everything starts at
 * version 1.
 *
 * Called once, when a solo sheet is first shared. Two peers doing this to two different sheets
 * and meeting in the same room would produce two version-1 sets that merge by content, which is
 * why joining a room loads the room's state rather than pushing the local one — see room.ts.
 */
export function fromScene(scene: Scene, by: PeerId, at = Date.now()): SyncScene {
  const ordered = inPaintOrder(scene);
  const keys = initialKeys(ordered.length);
  const records: Record<Id, SyncRecord> = {};
  ordered.forEach((object, i) => {
    records[object.id] = {
      id: object.id,
      object,
      deleted: false,
      version: 1,
      versionNonce: versionNonce(),
      index: keys[i]!,
      updatedBy: by,
      updatedAt: at,
    };
  });
  return { records, seen: [] };
}

// ── Turning a local edit into a commit ─────────────────────────────────────────────────────

export interface SceneDiff {
  added: SceneObject[];
  changed: SceneObject[];
  removed: Id[];
}

const sameObject = (a: SceneObject, b: SceneObject): boolean => canonical(a) === canonical(b);

/**
 * What changed between two snapshots of a scene.
 *
 * The seam into the existing app: every mutation in CanvasPage.vue already has a before and an
 * after, because every mutation is a `Command`. Diffing two snapshots means multiplayer needs no
 * new mutation path and cannot miss one — a command nobody remembered to instrument still shows
 * up here.
 *
 * `z` is ignored when deciding whether an object changed. It is derived from the sort key on the
 * way out, so a pure reshuffle of integers is not news; a real reorder changes the key instead.
 */
export function diffScenes(before: Scene, after: Scene): SceneDiff {
  const was = new Map(before.objects.map(o => [o.id, o] as const));
  const now = new Map(after.objects.map(o => [o.id, o] as const));
  const added: SceneObject[] = [];
  const changed: SceneObject[] = [];

  for (const object of after.objects) {
    const previous = was.get(object.id);
    if (!previous) added.push(object);
    else if (!sameObject({ ...previous, z: 0 }, { ...object, z: 0 })) changed.push(object);
  }
  const removed = before.objects.filter(o => !now.has(o.id)).map(o => o.id);
  return { added, changed, removed };
}

export interface CommitContext {
  room: string;
  peerId: PeerId;
  seq: number;
  label: string;
  at?: number;
}

export interface LocalCommit {
  scene: SyncScene;
  commit: Commit;
  /** What to push onto the undo stack to take this commit back. */
  undo: UndoEntry;
}

const highestKey = (records: Record<Id, SyncRecord>): string | null => {
  let top: string | null = null;
  for (const record of Object.values(records)) {
    if (record.deleted) continue;
    if (top === null || record.index > top) top = record.index;
  }
  return top;
};

/**
 * Apply a local change and produce the commit that tells everyone else about it.
 *
 * New objects are keyed above everything currently on the sheet — a stroke you just drew goes on
 * top, which is what a pen does. Edits keep the key they had, because moving an object does not
 * change what covers what.
 */
export function commitLocal(scene: SyncScene, diff: SceneDiff, context: CommitContext): LocalCommit {
  const at = context.at ?? Date.now();
  const records = { ...scene.records };
  const written: SyncRecord[] = [];
  const before: Record<Id, SyncRecord | null> = {};
  const wrote: Record<Id, number> = {};

  const write = (record: SyncRecord) => {
    before[record.id] = scene.records[record.id] ?? null;
    wrote[record.id] = record.version;
    records[record.id] = record;
    written.push(record);
  };

  const newKeys = keysBetween(highestKey(scene.records), null, diff.added.length);
  diff.added.forEach((object, i) => {
    const existing = scene.records[object.id];
    write({
      id: object.id,
      object,
      deleted: false,
      // An "add" of an id that already has a tombstone is an undo restoring it, and it has to
      // outrank the delete or the restore would lose to its own gravestone.
      version: (existing?.version ?? 0) + 1,
      versionNonce: versionNonce(),
      index: existing?.index ?? newKeys[i]!,
      updatedBy: context.peerId,
      updatedAt: at,
    });
  });

  for (const object of diff.changed) {
    const existing = scene.records[object.id];
    write({
      id: object.id,
      object,
      deleted: false,
      version: (existing?.version ?? 0) + 1,
      versionNonce: versionNonce(),
      index: existing?.index ?? keyAfter(highestKey(records)),
      updatedBy: context.peerId,
      updatedAt: at,
    });
  }

  for (const id of diff.removed) {
    const existing = scene.records[id];
    if (!existing) continue;
    write({
      ...existing,
      deleted: true,
      version: existing.version + 1,
      versionNonce: versionNonce(),
      updatedBy: context.peerId,
      updatedAt: at,
    });
  }

  const commit: Commit = {
    id: uuidv7(at),
    room: context.room,
    origin: context.peerId,
    seq: context.seq,
    at,
    label: context.label,
    records: written,
  };

  const seen = [...scene.seen, commit.id];
  if (seen.length > SEEN_LIMIT) seen.splice(0, seen.length - SEEN_LIMIT);

  return {
    scene: { records, seen },
    commit,
    undo: { commitId: commit.id, label: context.label, before, wrote },
  };
}

// ── Undo, which is the rule that matters ───────────────────────────────────────────────────

/**
 * How to take one of your own commits back.
 *
 * `before` is what each touched object looked like BEFORE the commit — `null` where it did not
 * exist. `wrote` is the version the commit stamped, and it is the whole basis of the safety
 * check: if an object's current version is higher than that AND somebody else wrote it, the undo
 * leaves that object alone.
 */
export interface UndoEntry {
  commitId: string;
  label: string;
  before: Record<Id, SyncRecord | null>;
  wrote: Record<Id, number>;
}

export interface UndoResult {
  scene: SyncScene;
  commit: Commit;
  /** Objects a peer has touched since. Left exactly as the peer left them. */
  skipped: Id[];
  /** Redo — the same shape, pointing the other way. */
  redo: UndoEntry;
}

/**
 * Undo as a forward edit.
 *
 * There is no rewinding a shared document. What ⌘Z does here is write your previous values again,
 * at a version above whatever is current, as a normal commit that everyone merges normally. That
 * is the only formulation that stays consistent when someone else is drawing at the same time,
 * and it is why an undone stroke reappears on the other screen the same way any other edit does.
 *
 * The one guard: an object a REMOTE peer has written since your commit is skipped. Their work
 * survives, and the caller is told which ids were left behind so the status strip can say so.
 */
export function applyUndo(scene: SyncScene, entry: UndoEntry, context: CommitContext): UndoResult {
  const at = context.at ?? Date.now();
  const records = { ...scene.records };
  const written: SyncRecord[] = [];
  const skipped: Id[] = [];
  const redoBefore: Record<Id, SyncRecord | null> = {};
  const redoWrote: Record<Id, number> = {};

  for (const [id, was] of Object.entries(entry.before)) {
    const current = records[id];
    const mine = current?.updatedBy === context.peerId;
    if (current && !mine && current.version > (entry.wrote[id] ?? 0)) {
      skipped.push(id);
      continue;
    }

    const version = (current?.version ?? entry.wrote[id] ?? 0) + 1;
    const restored: SyncRecord = was
      ? { ...was, version, versionNonce: versionNonce(), updatedBy: context.peerId, updatedAt: at }
      : {
          // It did not exist before the commit, so undoing means deleting it. The tombstone needs
          // a body, and the only body available is what is there now.
          ...(current ?? {
            id, object: undefined as unknown as SceneObject, index: keyAfter(highestKey(records)),
          } as SyncRecord),
          id,
          deleted: true,
          version,
          versionNonce: versionNonce(),
          updatedBy: context.peerId,
          updatedAt: at,
        };
    if (!restored.object) { skipped.push(id); continue; }

    redoBefore[id] = current ?? null;
    redoWrote[id] = version;
    records[id] = restored;
    written.push(restored);
  }

  const commit: Commit = {
    id: uuidv7(at),
    room: context.room,
    origin: context.peerId,
    seq: context.seq,
    at,
    label: context.label,
    records: written,
  };

  const seen = [...scene.seen, commit.id];
  if (seen.length > SEEN_LIMIT) seen.splice(0, seen.length - SEEN_LIMIT);

  return {
    scene: { records, seen },
    commit,
    skipped,
    redo: { commitId: commit.id, label: entry.label, before: redoBefore, wrote: redoWrote },
  };
}

/**
 * Whether an undo entry would still do anything.
 *
 * A ⌘Z that is going to skip every object it touches should not be spent — the stack pops to the
 * previous entry instead, which is what "undo my last action" means when your last action has
 * been overwritten by someone else.
 */
export function undoWouldDo(scene: SyncScene, entry: UndoEntry, peerId: PeerId): boolean {
  return Object.keys(entry.before).some(id => {
    const current = scene.records[id];
    if (!current) return false;
    return current.updatedBy === peerId || current.version <= (entry.wrote[id] ?? 0);
  });
}
